/**
 * Promote the Picturator upload cover into the TOP LAYER (NGE-13204).
 *
 * THE BUG THIS RETIRES. `.uploader-cover` is `position: fixed; inset: 0` and is rendered
 * INLINE inside `.upload-widget-embed` — the vendor bundle does not portal it to `<body>`.
 * A `position: fixed` box is sized by its nearest ancestor that establishes a containing
 * block, and `transform`, `translate`, `scale`, `rotate`, `filter`, `backdrop-filter`,
 * `perspective` and `will-change` on any of those all do that. So ONE transformed ancestor
 * anywhere above the CTA shrinks a full-screen overlay to that ancestor's box.
 *
 * Measured live on STIT (theme v2.37, `/pages/nl-am-wb`), 38 frames recorded during a real
 * upload: the cover spanned 415x658 at (66, -238) against a 1455x836 viewport for its whole
 * 3.3 s life, animating 415 -> 437 -> 415 as the vendor's `scale(.95) -> scale(1)` ran. A dark
 * panel pulsing inside one tile is what shoppers reported as flicker.
 *
 * The culprit was `transform: matrix(1, 0, 0, 1, 0, -3)` on `div.product-card` — i.e.
 * `.product-card:hover { transform: translateY(-3px) }` from `bestsellers.liquid`, which leaks
 * theme-wide because Shopify concatenates every `{% stylesheet %}` into ONE global asset. The
 * shopper clicks the CTA, the cursor stays on the card, and `:hover` holds that transform for
 * the entire upload.
 *
 * WHY A SELECTOR CANNOT BE THE FIX. Five per-site CSS patches have tried and the class keeps
 * coming back (NGE-9614, NGE-7761, theme.css, and two in PR #263). #263's rule —
 * `product-card:has(.uploader-cover.-visible)` — is a TYPE selector, so it DOES match the custom
 * element `<product-card>` (product-card.liquid gives it the class `product-card` as well). What
 * it cannot match is `campaign-bundle-tiles.liquid`'s hand-rolled `<div class="product-card">`,
 * a copy of the same card chrome — so the rule worked on the PDP/PLP card and missed the campaign
 * tile, which is the one measured above. That is the failure mode: every such fix has to name an
 * ancestor correctly, and the next copy of the chrome is written by someone who never saw it.
 *
 * WHAT THE TOP LAYER BUYS. A top-layer element is laid out against the viewport and is immune
 * to EVERY ancestor containing block and stacking context, so no selector has to name anything.
 * It also needs no DOM move: `showPopover()` promotes the node where it stands, leaving React's
 * reconciliation intact (relocating a React-owned node risks `NotFoundError` on unmount).
 *
 * `manual`, not `auto`: an `auto` popover light-dismisses on Escape and on an outside click,
 * which would let a shopper dismiss the overlay mid-upload while the upload kept running.
 *
 * Degrades safely. Where `showPopover` is missing the cover keeps the vendor's own
 * `position: fixed` behaviour — i.e. exactly what ships today — so this is never a regression.
 * Two fallback prongs keep the old stacking workarounds alive: `@supports not
 * selector(:popover-open)` for browsers that cannot parse the selector at all, and
 * `.-visible:not(:popover-open)` for a browser that can, but where promotion failed anyway.
 */

const COVER_SELECTOR = '.uploader-cover';

const supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';

const promote = (cover) => {
  if (!cover.isConnected) {
    return;
  }

  // Everything here is inside the try, `matches()` included: a browser that knows `showPopover`
  // but not the `:popover-open` selector would otherwise throw on the guard rather than the call.
  try {
    if (cover.getAttribute('popover') !== 'manual') {
      cover.setAttribute('popover', 'manual');
    }

    // showPopover() throws InvalidStateError on an already-open popover.
    if (cover.matches(':popover-open')) {
      return;
    }

    cover.showPopover();
  } catch (_) {
    // A cover that refuses to promote must never break the upload it is reporting on. It stays an
    // ordinary `position: fixed` box, which is exactly today's shipped behaviour — and the
    // `.-visible:not(:popover-open)` rules in theme.css and pdp-format-preview.liquid re-apply the
    // old stacking workarounds for precisely that state. Gating those on API support alone would
    // have left a failed promotion with neither the top layer nor the workaround.
  }
};

const promoteWithin = (node) => {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  if (node.matches(COVER_SELECTOR)) {
    promote(node);
  }

  for (const cover of node.querySelectorAll(COVER_SELECTOR)) {
    promote(cover);
  }
};

if (supportsPopover) {
  // A cover already in the DOM when this module runs (bfcache restore, re-executed bundle).
  promoteWithin(document.body);

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        promoteWithin(node);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
